import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../app/store";
import { fetchAccountOverviews } from "../logic/accountDetailsThunk";

import AccountSummary from "./AccountSummary";
import AccountSidePanel from "./AccountSidePanel";

import BorrowersSection from "../../stakeholder/components/BorrowerSection";

import { CustomLoadingSpinner } from "../../../components/ui/spinners/LoadingSpinner";
import { ErrorDisplay } from "../../../components/ui/error/ErrorDisplay";
import CreditAccountDropdown from "./creditAccountDropdown";
import GuarantorsSection from "../../stakeholder/components/GuarantorsSection";
import KeyPersonnelSection from "../../stakeholder/components/KeyPersonnelSection";
import KeyPersonnelSidePeek from "../../stakeholder/components/KeyPersonnelSidePeek";
import BorrowerSidePeek from "../../stakeholder/components/BorrowerSidePeek";
import GuarantorSidePeek from "../../stakeholder/components/GuarantorSidePeek";
import { getStakeholders } from "../../stakeholder/logic/stakeholderSlice";
import { fetchEnrichedStakeholder } from "../../stakeholder/logic/stakeholderThunks";

export type LoadingState = "idle" | "loading" | "failed";

type SidePanelType = "borrower" | "guarantor" | "keyPersonnel";

export type ActiveStakeholderSidePanelState = {
  stakeholderId: string;
  type: SidePanelType;
} | null;

type ActiveAccountSidePanelState = {
  accountNumber: string;
  instanceId: number;
} | null;

const AccountDetailsTab: React.FC = () => {
  const { id: caseId } = useParams<{ id: string }>();

  const dispatch = useAppDispatch();

  const allStakeholders = useAppSelector(getStakeholders);

  const [selectedAccountNumber, setSelectedAccountNumber] = useState<string | null>(null);

  const [activeAccountSidePanel, setActiveAccountSidePanel] = useState<ActiveAccountSidePanelState>(null);

  const [activeStakeholderSidePanel, setActiveStakeholderSidePanel] = useState<ActiveStakeholderSidePanelState>(null);

  const [loadingState, setLoadingState] = useState<LoadingState>("idle");

  const loadAccountOverviews = async () => {
    if (!caseId) {
      return;
    }

    setLoadingState("loading");

    const result = await dispatch(
      fetchAccountOverviews(caseId)
    );

    if (fetchAccountOverviews.fulfilled.match(result)) {
      setLoadingState("idle");
      return;
    }

    if (result.meta.condition) {
      setLoadingState("idle");
      return;
    }

    setLoadingState("failed");
  };

  useEffect(() => {
    loadAccountOverviews();
  }, [caseId]);

  useEffect(() => {
    if (!caseId || allStakeholders.length === 0) return;

    const stakeholderIds = allStakeholders.map((s) => s.stakeholder_id);
    dispatch(
      fetchEnrichedStakeholder({
        caseId,
        stakeholderIds,
      })
    );
  }, [caseId, allStakeholders, dispatch]);


  const handleCloseSidePanel = (
    stakeholderId: string,
    panelType: SidePanelType,
  ) => {
    setActiveStakeholderSidePanel((currentActiveSidePanel) => {
      if (
        currentActiveSidePanel?.stakeholderId !== stakeholderId ||
        currentActiveSidePanel?.type !== panelType
      ) {
        return currentActiveSidePanel;
      }

      return null;
    });
  };

  const openAccountSidePanel = (accountNumber: string) => {
    setSelectedAccountNumber(accountNumber);
    setActiveStakeholderSidePanel(null);

    setActiveAccountSidePanel((currentPanel) => ({
      accountNumber,
      instanceId: (currentPanel?.instanceId ?? 0) + 1,
    }));
  };

  const handleCloseAccountSidePanel = (instanceId: number) => {
    setActiveAccountSidePanel((currentPanel) => {
      if (currentPanel?.instanceId !== instanceId) {
        return currentPanel;
      }

      return null;
    });
  };

  const handleAccountClickFromSidePanel = (
    accountNo: string,
  ) => {
    openAccountSidePanel(accountNo);
  };

  if (loadingState === "loading") {
    return (
      <div className="p-3">
        <CustomLoadingSpinner
          title="Loading account overview"
          subtitle="Fetching account details..."
          minHeight="min-h-[350px]"
        />
      </div>
    );
  }

  if (loadingState === "failed") {
    return (
      <div className="p-3">
        <ErrorDisplay
          error="Failed to load account overview data. Please retry."
          onRetry={loadAccountOverviews}
        />
      </div>
    );
  }

  const sidePanelKey = activeStakeholderSidePanel
    ? `${activeStakeholderSidePanel.stakeholderId}-${activeStakeholderSidePanel.type}`
    : undefined;

  return (
    <div className="flex w-full flex-col">
      <div className="flex items-center bg-[#E5E7EB] p-3">
        <CreditAccountDropdown
          selectedAccountNumber={selectedAccountNumber ?? undefined}
          onAccountSelectionChange={(accountNumber) => {
            setSelectedAccountNumber(accountNumber);
            setActiveStakeholderSidePanel(null);
          }}
        />
      </div>

      {selectedAccountNumber ? (
        <div
          className="flex w-full flex-col gap-6 bg-white p-4"
          style={{
            borderRadius: "0 0 14px 14px",
          }}
        >
          <AccountSummary
            accountNumber={selectedAccountNumber}
            onViewDetailsClick={() =>
              openAccountSidePanel(selectedAccountNumber)
            }
          />

          <BorrowersSection
            accountNumber={selectedAccountNumber}
            onBorrowerClick={(stakeholderId) =>
              setActiveStakeholderSidePanel({
                stakeholderId,
                type: "borrower",
              })
            }
          />

          <GuarantorsSection
            accountNumber={selectedAccountNumber}
            onGuarantorClick={(stakeholderId) =>
              setActiveStakeholderSidePanel({
                stakeholderId,
                type: "guarantor",
              })
            }
          />

          <KeyPersonnelSection
            accountNumber={selectedAccountNumber}
            onDirectorClick={(stakeholderId) =>
              setActiveStakeholderSidePanel({
                stakeholderId,
                type: "keyPersonnel",
              })
            }
          />
        </div>
      ) : (
        <div
          className="flex min-h-[250px] w-full flex-col items-center justify-center gap-1.5 bg-white p-8 text-center"
          style={{
            borderRadius: "0 0 14px 14px",
          }}
        >
          <p className="text-sm font-medium text-[#0A0A0A]">
            No loan accounts available
          </p>
          <p className="text-xs font-normal text-[#737373]">
            There are no loan accounts linked to this case.
          </p>
        </div>
      )}

      {activeAccountSidePanel && (
        <AccountSidePanel
          key={activeAccountSidePanel.instanceId}
          accountNumber={activeAccountSidePanel.accountNumber}
          onClose={() =>
            handleCloseAccountSidePanel(
              activeAccountSidePanel.instanceId,
            )
          }
          onBorrowerClick={(stakeholderId) =>
            setActiveStakeholderSidePanel({
              stakeholderId,
              type: "borrower",
            })
          }
        />
      )}

      {selectedAccountNumber && activeStakeholderSidePanel && (
        <>
          {activeStakeholderSidePanel.type === "borrower" && (
            <BorrowerSidePeek
              key={sidePanelKey}
              stakeholderId={activeStakeholderSidePanel.stakeholderId}
              onClose={() =>
                handleCloseSidePanel(
                  activeStakeholderSidePanel.stakeholderId,
                  "borrower",
                )
              }
              onAccountClick={
                handleAccountClickFromSidePanel
              }
            />
          )}

          {activeStakeholderSidePanel.type === "guarantor" && (
            <GuarantorSidePeek
              key={sidePanelKey}
              stakeholderId={activeStakeholderSidePanel.stakeholderId}
              onClose={() =>
                handleCloseSidePanel(
                  activeStakeholderSidePanel.stakeholderId,
                  "guarantor",
                )
              }
              onAccountClick={
                handleAccountClickFromSidePanel
              }
            />
          )}

          {activeStakeholderSidePanel.type ===  "keyPersonnel" && (
            <KeyPersonnelSidePeek
              key={sidePanelKey}
              stakeholderId={activeStakeholderSidePanel.stakeholderId}
              onClose={() =>
                handleCloseSidePanel(
                  activeStakeholderSidePanel.stakeholderId,
                   "keyPersonnel",
                )
              }
              onAccountClick={
                handleAccountClickFromSidePanel
              }
            />
          )}
        </>
      )}
    </div>
  );
};

export default AccountDetailsTab;